import type { TransitionOptions, RawParams, HrefOptions, StateOrName, TransitionPromise } from "@uirouter/core";
import { RejectType } from "@uirouter/react";
import type { UIRouterReact } from "@uirouter/react";
import { isObject, defaults, isString, take, merge, find } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { apiHelper } from "app/common/services/apiHelper";
import type { Solutions2Package } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { PREMIUM_FEATURES_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import DurationService from "app/services/DurationService";

import { lazyLoadManager } from "./lazyLoadManager";
import type { IRouterState, IRouterStateWithName } from "./router/IRouterState";

const HISTORY_SIZE = 1;

export interface IRouteHistoryEntry {
    state: IRouterState;
    params: any;
}

const lockedStateParams = ["duration", "comparedDuration", "country", "webSource"];
let router: UIRouterReact;
const routeHistory: IRouteHistoryEntry[] = new Array(HISTORY_SIZE).fill(null); // 0 index is last, and so on

const getRouteHistory = (index?: number) => {
    return index === undefined ? routeHistory : routeHistory[index];
};
const init = (routerInstance: UIRouterReact) => {
    router = routerInstance;
    lazyLoadManager.init(routerInstance);
};
const getState = (stateOrName: StateOrName): IRouterState => {
    return router.stateService.get(stateOrName) as IRouterState;
};

/** @deprecated Use {@link hrefAsync} instead */
const href = (stateOrName: StateOrName, params?: RawParams, options?: HrefOptions) => {
    void lazyLoadManager.lazyLoadIfNeeded(stateOrName);
    return router.stateService.href(stateOrName, params, options);
};

const hrefAsync = async (stateOrName: StateOrName, params?: RawParams, options?: HrefOptions) => {
    await lazyLoadManager.lazyLoadIfNeeded(stateOrName);
    return router.stateService.href(stateOrName, params, options);
};

const getStateUrl = (state: StateOrName, params?: RawParams) => {
    let newPath = router.stateService.href(state, params);
    if (!newPath) {
        // This is a solution for when there is no $state configured
        let stateObj = getState(state);
        if (!stateObj) {
            return "";
        }
        let url = stateObj.url;
        // append state parents
        while (stateObj && stateObj.parent) {
            // @ts-ignore
            stateObj = stateObj.parentObj as IRouterState;
            // stateObj = getState(stateObj.parent, 'state');
            if (stateObj && stateObj.url) {
                url = stateObj.url + url;
            }
        }
        // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'string' — url accumulates from optional stateObj.url and may stay undefined when no parent has url; legacy edge case
        const urlMatcher = router.urlMatcherFactory.compile(url);
        newPath = router.urlRouter.href(urlMatcher, params, { absolute: true });
    }

    return decodeURIComponent(newPath);
};

const getPropertyValueFromHierarchy = (stateOrName: string | IRouterState, property: string): any => {
    if (!stateOrName) {
        return null;
    }
    const pageState = getState(stateOrName);
    // @ts-ignore
    return pageState[property] ?? getPropertyValueFromHierarchy(pageState.parent, property);
};

const current = () => {
    return (router.globals.current || {}) as IRouterState;
};

const getNearestStateWithHOF = (attribute: keyof IRouterState) => (state: IRouterState) => {
    return getNearestStateWith(attribute, state);
};

const getNearestStateWith = (attribute: keyof IRouterState, state: IRouterState): IRouterState | null => {
    if (state[attribute]) {
        return state;
    }
    const parentState = state.parent ? getState(state.parent) : null;
    return parentState ? getNearestStateWith(attribute, parentState) : null;
};

const getNearestStateWithSecondaryBarType3 = getNearestStateWithHOF("secondaryBarType3");
const getNearestStateWithSecondaryBarType = getNearestStateWithHOF("secondaryBarType");

/**
 * Retreives the current nav item query params, merged with
 * the current state nav params
 */
const getItemParams = (navItem: any) => {
    if (!navItem || (!navItem.state && !navItem.route)) {
        return {};
    }

    const stateParams = getParams();

    // delete some params
    delete stateParams.page;
    delete stateParams.filter;
    delete stateParams.orderby;
    delete stateParams.orderBy;

    const stateObj = getState(navItem.state);
    const configId = getConfigId(stateObj);
    const configObj = configId && swSettings.components[configId];

    // fill default params from config, if the key isn't presented in current state params
    if (configObj && configObj.defaultParams) {
        defaults(stateParams, configObj.defaultParams);
    }

    // add default query params
    const defaultQueryParams = {};
    if (stateObj && stateObj.defaultQueryParams) {
        Object.assign(defaultQueryParams, stateObj.defaultQueryParams);
    }
    if (navItem && navItem.defaultQueryParams) {
        Object.assign(defaultQueryParams, navItem.defaultQueryParams);
    }
    lockedStateParams.map((property) => {
        if (stateParams.hasOwnProperty(property) && defaultQueryParams.hasOwnProperty(property)) {
            // @ts-ignore
            delete defaultQueryParams[property];
        }
    });

    Object.assign(stateParams, defaultQueryParams);
    merge(stateParams, navItem.overrideParams);

    return stateParams;
};

const getParams = (): Record<string, any> => {
    return { ...router?.globals?.params };
};

const getConfigId = (state: IRouterState, params?: RawParams): string | null => {
    if (!state || (!state.configId && !state.parent && !state.childStates)) {
        return null;
    }
    if (!state.configId) {
        if (state.name != null) {
            void lazyLoadManager.lazyLoadIfNeeded(state.name);
        }
        // Try childStates first if params are available
        if (state.childStates && params) {
            const childConfigId = getConfigIdFromChildStates(state.childStates, params.webSource, params.selectedTab);
            if (childConfigId) {
                return childConfigId;
            }
        }
        // Fall back to parent
        if (state.parent) {
            return getConfigId(getState(state.parent), params);
        } else {
            return null;
        }
    } else {
        if (typeof state.configId === "function") {
            // @ts-ignore FIXME: strict migration - Type 'undefined' is not assignable to type 'RawParams'
            return state.configId(state, params);
        } else {
            return state.configId;
        }
    }
};

const getConfigIdFromStateConfig = (toState: IRouterState, toParams: RawParams): string => {
    let configId = typeof toState.name === "string" ? getConfigId(getState(toState.name), toParams) : "";
    if (!configId) {
        // try from childStates
        configId = getConfigIdFromChildStates(toState.childStates, toParams.webSource, toParams.selectedTab);
        if (!configId) {
            // read from state itself
            configId = getConfigId(toState, toParams);
            // possible sub component
            configId = getAppStoreSubComponent(configId, toParams);
        }
    }
    // @ts-ignore FIXME: strict migration - 'string | null' not assignable to 'string' — getConfigId can return null but downstream consumers treat the returned value as a possibly-empty string
    return configId;
};

const getConfigIdFromChildStates = (configObj: any, webSource: any, selectedTab: any) => {
    webSource = isString(webSource) ? webSource.toLowerCase() : "desktop";
    selectedTab = isString(selectedTab) ? selectedTab.toLowerCase() : "pages";
    if (typeof configObj === "function") {
        return configObj({ webSource, selectedTab });
    }
    // childStates configObj is { webSource: { configId: "id" } } or { webSource: { selectedTab: { configId: "id" } } }
    if (isObject(configObj)) {
        // try webSource
        if (webSource in configObj) {
            // @ts-ignore
            configObj = configObj[webSource];
        }
        // try selectedTab
        if (selectedTab in configObj) {
            configObj = configObj[selectedTab];
        }
        // error
        if (!configObj || !configObj.configId) {
            return null;
        }
        // Resolve function if needed
        return typeof configObj.configId === "function"
            ? configObj.configId(configObj, { webSource, selectedTab })
            : configObj.configId;
    }
    return null;
};
const getAppStoreSubComponent = (configId: any, toParams: any) => {
    const appStore = getComponentAppStoreSuffix(toParams);

    if (appStore === "Android" && configId === "AppEngagementUserActivityIOS") {
        return "AppEngagementOverview";
    }

    if (!appStore) {
        return configId;
    }
    const subComponent = configId + appStore;
    if (subComponent in swSettings.components) {
        return subComponent;
    }
    return configId;
};
const getComponentAppStoreSuffix = (toParams: any) => {
    const params = toParams || router.globals.params;
    if (params.appId) {
        return params.appId.charAt(0) === "1" ? "IOS" : "Android";
    }
    return "";
};
/**
 * checks the params, modifies them if required, and returns the modified object if modified, and null if not.
 * TODO: this should be per param per module
 *
 * @param origPrams
 * @returns {{} | null}
 */
const validateParams = (origPrams: any, state: any) => {
    const params = { ...origPrams };
    let propName;
    let testedValue;
    const configId = getConfigId(state);
    const configObj = configId && swSettings.components[configId];
    if (params.key) {
        propName = "key";
        testedValue = params.key;
    }
    if (params.appId) {
        propName = "appId";
        testedValue = params.appId;
    }
    if (state && state.minDurationRange) {
        const { from, to } = DurationService.getDurationData(
            origPrams.duration,
            undefined,
            configId ?? undefined,
        ).forAPI;
        // @ts-ignore FIXME: getDiffSymbol expects Dayjs but forAPI.from/to are strings — callers should use raw (Dayjs) dates instead
        const diffSymbol = DurationService.getDiffSymbol(from, to);
        const diffNumber = parseInt(diffSymbol.replace("m", ""), 10);
        if (diffNumber < state.minDurationRange) {
            params.duration = configObj.defaultParams.duration;
            return params;
        }
    }

    if (testedValue) {
        const parts = testedValue.split(",");
        const mainPart = parts[0];
        switch (propName) {
            case "key":
                if (configObj && configObj.isDemo && mainPart !== configObj.demo.website) {
                    params[propName] = configObj.demo.website;
                    return params;
                }
                break;
            case "appId":
                if (configObj && configObj.isDemo && mainPart !== configObj.demo.appId) {
                    params[propName] = configObj.demo.appId;
                    return params;
                }
                break;
        }
        const allowedCompetitorsCount = Number(configObj?.resources?.AllowedCompetitorsCount);
        if (parts.length > allowedCompetitorsCount + 1) {
            // @ts-ignore FIXME: strict migration - 'undefined' cannot be used as an index type — propName is set whenever testedValue is set, but the narrowing is not visible to TS here
            params[propName] = take(parts, allowedCompetitorsCount + 1).join();
            return params;
        } else {
            return null;
        }
    }
    return null;
};

const getApiParams = (params?: any, componentId?: string) => {
    const rawParams = params || getParams();
    return apiHelper.transformParamsForAPI(rawParams, componentId);
};

interface IRawParamsWithFlags extends RawParams {
    key?: string;
    isVirtual?: boolean;
}

const updateParams = <P extends IRawParamsWithFlags = IRawParamsWithFlags>(params: P, options?: any) => {
    if (params.isVirtual && params.key) {
        params.key = "*" + params.key;
    }
    return go(router.globals.current, params, {
        inherit: true,
        ...options,
    });
};

/**
 * Handles UI-Router transition rejections by filtering out expected rejection types.
 *
 * UI-Router throws rejections for various scenarios:
 * - SUPERSEDED: A new transition superseded this one (normal behavior)
 * - ABORTED: Transition was explicitly aborted (normal behavior)
 * - IGNORED: Transition was ignored by a hook (normal behavior)
 *
 * These are expected scenarios and should not be treated as errors.
 * Any other rejection type is re-thrown for proper error handling.
 *
 * @param transitionPromise - The transition promise to handle
 * @returns Promise resolving to StateObject or undefined
 */
const handleTransitionRejection = (transitionPromise: TransitionPromise) =>
    transitionPromise.catch((error) => {
        if (error && typeof error === "object" && "type" in error) {
            const rejectType = error.type;
            if ([RejectType.IGNORED, RejectType.ABORTED, RejectType.SUPERSEDED].includes(rejectType)) {
                return undefined;
            }
        }
        throw error;
    });

const go = async <P extends RawParams = RawParams>(
    stateOrName: StateOrName,
    params?: P,
    options?: TransitionOptions,
) => {
    if (stateOrName === undefined || stateOrName === null || stateOrName === "undefined" || stateOrName === "null") {
        Sentry.errorTracker({
            e: new Error("Attempted navigation to corrupted state"),
            message: "Attempted navigation to corrupted state",
            level: "error",
            params: {
                extra: {
                    stateOrName,
                    params,
                    options,
                    currentState: router?.globals?.current?.name,
                },
            },
        });
        return Promise.resolve();
    }

    const transitionTo = (stateOrName: StateOrName, params?: P, options?: TransitionOptions) => {
        // Add source: "sref" so the transition has a proper source instead of "unknown"
        // "sref" is the appropriate source for programmatic navigation (similar to link clicks)
        const optionsWithSource: TransitionOptions = { source: "sref", ...options };
        const transitionPromise = router.stateService.go(stateOrName, params, optionsWithSource);
        return handleTransitionRejection(transitionPromise);
    };
    try {
        const currentTransition = router.globals.transition;
        // When calling .go() before a previous call to .go is done,
        // We need to cancel the previous transition, And then to activate the new one
        if (currentTransition && !currentTransition.success) {
            const transitionParams = currentTransition.params() || {};
            currentTransition.abort();
            return currentTransition.promise.catch(async () => {
                await lazyLoadManager.lazyLoadIfNeeded(stateOrName);
                // @ts-ignore FIXME: strict migration - merged params widen to '{ [x: string]: any }' which TS can't reconcile with generic P
                return transitionTo(stateOrName, { ...transitionParams, ...params }, options);
            });
        } else {
            await lazyLoadManager.lazyLoadIfNeeded(stateOrName);
            return transitionTo(stateOrName, params, options);
        }
    } catch {
        return transitionTo(stateOrName, params, options);
    }
};

const updateQueryParams = (obj: any, options: any = {}) => {
    const defaultOptions = {
        location: true,
        reload: true,
    };
    options = [defaultOptions, options].reduce(Object.assign, {});
    go(router.globals.current, Object.assign(router.globals.params, obj), options);
};

const isWebsiteAnalysis = (state?: IRouterState) => {
    state = state || current();
    const matcher =
        /^website|searchadvertising|companyresearch_website|ranktracker\.|brandmonitoring\.|adintelligence-|salesIntelligence-advertiseractivity|aitraffic\.frame|accountreview_website|accountreview_website_dev|digitalsuite_website|organicsearch_website|paidsearch_website|digitalsuite_pageAnalysis/;
    return state.parent ? !!state.parent.match(matcher) : !!state.name?.match(matcher);
};

/**
 * Checks if the current state is a dashboard
 * @returns {boolean}
 */
const isDashboard = () => {
    const state = current();
    return (state.parent && !!state.parent.match(/^dashboard/)) || !!state.name?.match(/dashboard/);
};

const isIndustryAnalysis = (state?: IRouterState): boolean => {
    state = state || current();
    return (
        !!state.parent &&
        !!state.parent.match(
            /^industry|market_research_pages_abstract_parent|market_research_abstract_parent_v2|marketresearch_webmarketanalysis|marketresearch_webmarketanalysis_rankings_root|digitalsuite_marketresearch_webmarketanalysis/,
        )
    );
};

const getNearestStateWithSecondaryOpenStatus = (state: IRouterState): IRouterState | null => {
    if (typeof state.isSecondaryBarOpen !== "undefined") {
        return state;
    }

    const stateParent = state.parent ? getState(state.parent) : null;
    return stateParent ? getNearestStateWithSecondaryOpenStatus(stateParent) : null;
};

const getNearestStateWithSecondaryOpenStatus3 = (state: IRouterState): IRouterState | null => {
    if (typeof state.isSecondaryBar3Open !== "undefined") {
        return state;
    }

    const stateParent = state.parent ? getState(state.parent) : null;
    return stateParent ? getNearestStateWithSecondaryOpenStatus3(stateParent) : null;
};

const getStateForPageId = (pageId: any) => {
    const allStates = router.stateRegistry.get();
    const state = find(allStates, {
        section: pageId?.section,
        subSection: pageId?.subSection,
        subSubSection: pageId?.subSubSection,
    });
    return state;
};

const getCurrentModule = () => {
    const currModule = current()?.name;
    if (currModule && currModule.indexOf(".") > 0) {
        return currModule.split(".")[0];
    }
    if (currModule && currModule.indexOf("-") > 0) {
        return currModule.split("-")[0];
    }

    // The newer option is _ separation
    return currModule?.split("_")[0];
};

/** @deprecated Use {@link updateParams} instead */
const applyUpdateParams = <P extends IRawParamsWithFlags = IRawParamsWithFlags>(
    params: P,
    options?: TransitionOptions,
) => {
    updateParams(params, options);
};

const applyUpdateParamsAsync = <P extends IRawParamsWithFlags = IRawParamsWithFlags>(
    params: P,
    options?: TransitionOptions,
) => {
    queueMicrotask(() => {
        applyUpdateParams(params, options);
    });
};

const getPackageName = (state: IRouterState): Solutions2Package | null => {
    // In case the current state contains this field - then the search is over <3
    if (state.packageName) {
        return state.packageName;
    }

    // otherwise - search through the state tree upwards, until a state with the bar type field
    // is found, or until we reached the top of the tree without finding any such state.
    const stateParent = state.parent ? getState(state.parent) : null;
    return stateParent ? getPackageName(stateParent) : null;
};

/**
 * @ngdoc function
 * check if the current state is a app analysis state
 * @returns {boolean}
 */
const isAppAnalysis = (state?: any): boolean => {
    state = state || current();

    return state.parent && !!state.parent.match(/^apps|digitalsuite_app|salesIntelligence-apps|salesIntelligence_app/);
};

const isKeywordAnalysis = (state?: IRouterState): boolean => {
    state = state || current();
    const parentPath = state?.parent ?? state?.name?.split(".").slice(0, -1).join(".");
    return (
        (!!parentPath && parentPath.includes("keywordAnalysis")) ||
        !!parentPath?.includes("findkeywords_keywordGeneratorTool_root") ||
        !!parentPath?.includes("marketresearch_keywordmarketanalysis_search") ||
        !!parentPath?.includes("adintelligence-searchadvertising.root.keywordFrame")
    );
};

const isAppCategory = (state: any): boolean => {
    state = state || current();
    return (
        state.parent &&
        (!!state.parent.match(/^appcategory|appmarketanalysis/) ||
            state.parent.includes("salesIntelligence-appcategory"))
    );
};
const isIndustryConversion = (state: any): boolean => {
    state = state || current();
    return (state.parent && !!state.parent.match(/^conversion/)) || (state.name && !!state.name.match(/^conversion/));
};
const isLeadGenerator = (state?: IRouterState): boolean => {
    state = state || current();
    return (state.parent && !!state.parent.match(/^leadGenerator/)) || !!state.name?.match(/leadGenerator/);
};
const isSalesIntelligence = (state?: IRouterState): boolean => {
    state = state || current();
    return (state.parent && !!state.parent.match(/^salesIntelligence/)) || !!state.name?.match(/salesIntelligence/);
};

const getCurrentPageId = () => {
    return current().pageId;
};

/**
 * @ngdoc function
 * get the module of the state
 * @returns {string}
 */
const getStateModule = (state: IRouterStateWithName) => {
    const moduleName = state.name.split(state.name.indexOf(".") > 0 ? "." : "_")[0];
    if (moduleName.endsWith("-root")) {
        return moduleName.replace("-root", "");
    }
    return moduleName;
};

const getAllStates = () => {
    return router.stateService.get() as IRouterStateWithName[];
};

const getModuleStates = (module: string) => {
    return getAllStates().filter((state) => state.pageTitle && state.name.indexOf(module) !== -1);
};

/**
 * Used by new sidebar to build dynamic links (given different filters etc.)
 * @param menuItem - single item (child/parent) in a sidebar
 * @returns {string}
 */
const navLink = (menuItem: any, decodeHref: any) => {
    if (!menuItem || (!menuItem.state && !menuItem.route)) {
        return "";
    }

    const currentState = current();
    const stateParams = getParams();
    let hrefStr;
    const itemName = menuItem.state;

    if (!currentState.pageId) {
        return "";
    }

    if (currentState.pageId.section === "empty") {
        return "javascript:void(0)";
    }

    // delete some params
    delete stateParams.page;
    delete stateParams.filter;
    delete stateParams.orderby;
    delete stateParams.orderBy;

    const stateObj = getState(itemName);
    const configId = getConfigId(stateObj);
    const configObj = configId && swSettings.components[configId];

    // fill default params from config, if the key isn't presented in current state params
    if (configObj && configObj.defaultParams) {
        defaults(stateParams, configObj.defaultParams);
    }

    // add default query params
    const defaultQueryParams = {};
    if (stateObj && stateObj.defaultQueryParams) {
        Object.assign(defaultQueryParams, stateObj.defaultQueryParams);
    }
    if (menuItem && menuItem.defaultQueryParams) {
        Object.assign(defaultQueryParams, menuItem.defaultQueryParams);
    }
    lockedStateParams.map((property) => {
        if (stateParams.hasOwnProperty(property) && defaultQueryParams.hasOwnProperty(property)) {
            // @ts-ignore
            delete defaultQueryParams[property];
        }
    });
    Object.assign(stateParams, defaultQueryParams);

    merge(stateParams, menuItem.overrideParams);

    hrefStr = href(itemName, stateParams);
    if (decodeHref !== false) {
        hrefStr = decodeURIComponent(hrefStr);
    }

    return hrefStr;
};

const reload = (stateName?: any) => {
    router.stateService.reload(stateName);
};

// TODO: do we need types here?
// type Navigator = {
//     current: () => IRouterState;
//     href: (stateOrName: StateOrName, params?: RawParams, options?: HrefOptions) => string;
//     getApiParams: (params?: any) =>
// };

/**
 * @ngdoc function
 * check if the current state is a home page
 * @returns {boolean}
 */
const isHomePage = (state?: IRouterState) => {
    state = state || current();
    return (state.parent && !!state.parent.match(/^home/)) || !!state.name?.match(/home/);
};
const isGooglePlayKeywordAnalysis = (state?: IRouterState) => {
    state = state || current();
    return state.parent && state.parent.includes("keywords");
};
/**
 * ported from similarweb.utils
 * used for website search in sw-universal-search directive
 * @param searchTerm
 * @returns {*}
 */
const getValidSearchTerm = (searchTerm: string, shouldAddCom: any) => {
    const dots = searchTerm.split(".").length - 1;
    if (!searchTerm) {
        return "";
    }
    const invalidDomainChars = /[\s,!@#$%^&*()_=+]+/g;
    /*                if (invalidDomainChars.test(searchTerm)) {
        return null;
        }*/
    searchTerm = searchTerm.replace(invalidDomainChars, "");
    searchTerm = searchTerm.trim().toLowerCase();
    const q = searchTerm.indexOf("?");
    if (q !== -1) {
        searchTerm = searchTerm.substr(0, q);
    }
    if (searchTerm.indexOf("http://") === 0) {
        searchTerm = searchTerm.substr(7);
    }
    if (searchTerm.indexOf("https://") === 0) {
        searchTerm = searchTerm.substr(8);
    }
    const directories = searchTerm.indexOf("/");
    if (directories !== -1) {
        searchTerm = searchTerm.substr(0, directories);
    }
    if (dots === 0 && !!shouldAddCom) {
        searchTerm += ".com";
    }
    if (searchTerm.slice(-1) === "." && dots === 1) {
        searchTerm += "com";
    }
    if (searchTerm.toLowerCase().indexOf("www.") === 0) {
        searchTerm = searchTerm.replace("www.", "");
    }

    return searchTerm;
};

const isOldSales = (state?: IRouterState) => {
    const _state = state || current();
    return _state.name === "salesWorkspace" && _state.absoluteUrl?.includes("/workspace/sales?");
};

const isAccountReview = (state?: IRouterState) => {
    const _state = state || current();
    return !!_state.name?.includes("accountreview");
};

const isSalesSolution = (state?: IRouterState) => {
    state = state || current();
    return isSalesIntelligence(state) || isAccountReview(state);
};

const isSalesCompanyAnalysis = (state?: IRouterState) => {
    const _state = state || current();
    return !!_state.name?.includes("salesIntelligence-company-analysis");
};

const isSalesPremiumFeatures = (state?: IRouterState) => {
    const _state = state || current();
    return !!_state.name?.includes(PREMIUM_FEATURES_PAGE_ROUTE);
};

const isSubNavOpen = (state?: IRouterState): boolean => {
    state ||= current();
    return state.secondaryBarType3 !== SecondarySideBarType3.None;
};

const isCurrentStateName = (stateName: string, state?: IRouterState): boolean => {
    const currentState = state || current();
    return currentState.name === stateName;
};

export const proNavigator = {
    current,
    /** @deprecated Use {@link hrefAsync} instead */
    href,
    hrefAsync,
    getAllStates,
    getApiParams,
    getItemParams,
    getConfigId,
    getConfigIdFromStateConfig,
    getCurrentModule,
    getCurrentPageId,
    getNearestStateWithSecondaryOpenStatus,
    getNearestStateWithSecondaryOpenStatus3,
    getPackageName,
    getParams,
    getState,
    getStateForPageId,
    getStateUrl,
    getNearestStateWithSecondaryBarType,
    getNearestStateWithSecondaryBarType3,
    getNearestStateWithHOF,
    getPropertyValueFromHierarchy,
    init,
    isAppAnalysis,
    isAppCategory,
    isIndustryAnalysis,
    isKeywordAnalysis,
    isLeadGenerator,
    isIndustryConversion,
    isOldSales,
    isWebsiteAnalysis,
    isDashboard,
    updateParams,
    validateParams,
    /** @deprecated Use {@link updateParams} instead */
    applyUpdateParams,
    applyUpdateParamsAsync,
    go,
    navLink,
    reload,
    updateQueryParams,
    getRouteHistory,
    isHomePage,
    isGooglePlayKeywordAnalysis,
    getValidSearchTerm,
    getStateModule,
    getModuleStates,
    isAccountReview,
    isSalesIntelligence,
    isSalesSolution,
    isSalesCompanyAnalysis,
    isSalesPremiumFeatures,
    isSubNavOpen,
    isCurrentStateName,
    lazyLoadIfNeeded: lazyLoadManager.lazyLoadIfNeeded,
};

export type NavigatorType = typeof proNavigator;
